// src/components/ProjectManagement/WorkerTasksModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

const WorkerTasksModal = ({ worker, onClose }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState('grid');
  
  // NEW: Filter state
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchWorkerTasks = async () => {
      if (!worker?.id) return;
      
      // Query tasks where assignedTo ARRAY CONTAINS worker.id
      const q = query(
        collection(db, 'tasks'),
        where('assignedTo', 'array-contains', worker.id)
      );
      
      const snapshot = await getDocs(q);
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().deadline?.toDate?.() || null,
        startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
        endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
        completedAtObj: doc.data().completedAt?.toDate?.() || null
      }));
      
      // Also fetch legacy tasks where assignedTo is a single string
      const legacyQuery = query(
        collection(db, 'tasks'),
        where('assignedTo', '==', worker.id)
      );
      const legacySnapshot = await getDocs(legacyQuery);
      const legacyTasks = legacySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().deadline?.toDate?.() || null,
        startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
        endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
        completedAtObj: doc.data().completedAt?.toDate?.() || null
      }));
      
      // Combine and remove duplicates
      const allTasks = [...tasksData, ...legacyTasks];
      const uniqueTasks = allTasks.filter((task, index, self) => 
        index === self.findIndex(t => t.id === task.id)
      );
      
      setTasks(uniqueTasks);
      setLoading(false);
    };
    
    fetchWorkerTasks();
  }, [worker]);

  // NEW: Memoized filtered tasks by status
  const filteredTasks = useMemo(() => {
    const now = new Date();
    
    if (statusFilter === 'all') return tasks;
    
    return tasks.filter(task => {
      switch (statusFilter) {
        case 'pending':
          return task.status === 'pending' && !(task.dueDate && task.dueDate < now);
        case 'ongoing':
          return task.status === 'ongoing';
        case 'completed':
          return task.status === 'completed';
        case 'overdue':
          return task.status === 'pending' && task.dueDate && task.dueDate < now;
        case 'qc-pending':
          return task.status === 'completed' && !task.qcVerified;
        case 'verified':
          return task.status === 'completed' && task.qcVerified;
        default:
          return true;
      }
    });
  }, [tasks, statusFilter]);

  // Memoized counts for filters
  const counts = useMemo(() => {
    const now = new Date();
    return {
      all: tasks.length,
      pending: tasks.filter(t => t.status === 'pending' && !(t.dueDate && t.dueDate < now)).length,
      ongoing: tasks.filter(t => t.status === 'ongoing').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(t => t.status === 'pending' && t.dueDate && t.dueDate < now).length,
      qcPending: tasks.filter(t => t.status === 'completed' && !t.qcVerified).length,
      verified: tasks.filter(t => t.status === 'completed' && t.qcVerified).length
    };
  }, [tasks]);

  const getTaskColor = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return '#10b981';
    if (task.status === 'completed' && !task.qcVerified) return '#f59e0b';
    if (task.status === 'ongoing') return '#3b82f6';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate < new Date()) return '#ef4444';
      return '#f59e0b';
    }
    return '#ebedf0';
  }, []);

  const getStatusText = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return 'Verified';
    if (task.status === 'completed' && !task.qcVerified) return 'QC Pending';
    if (task.status === 'ongoing') return 'In Progress';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate < new Date()) return 'Overdue';
      return 'Pending';
    }
    return task.status;
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return 'No date';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  }, []);

  const handleTaskHover = useCallback((e, task) => {
    setHoveredTask(task);
    setHoverPosition({ x: e.clientX + 20, y: e.clientY - 180 });
  }, []);

  const handleTaskLeave = useCallback(() => {
    setHoveredTask(null);
  }, []);

  // Filter chips component
  const FilterChips = () => (
    <div className="modal-filter-chips" style={{ 
      display: 'flex', 
      gap: '8px', 
      flexWrap: 'wrap',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #eef2f6'
    }}>
      <button 
        className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
        onClick={() => setStatusFilter('all')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'all' ? '#800020' : '#f3f4f6',
          color: statusFilter === 'all' ? 'white' : '#6b7280',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        All ({counts.all})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'pending' ? 'active' : ''}`}
        onClick={() => setStatusFilter('pending')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'pending' ? '#f59e0b' : '#fef3c7',
          color: statusFilter === 'pending' ? 'white' : '#92400e',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        ⏳ Pending ({counts.pending})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'ongoing' ? 'active' : ''}`}
        onClick={() => setStatusFilter('ongoing')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'ongoing' ? '#3b82f6' : '#dbeafe',
          color: statusFilter === 'ongoing' ? 'white' : '#1e40af',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        ⚙️ Ongoing ({counts.ongoing})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'overdue' ? 'active' : ''}`}
        onClick={() => setStatusFilter('overdue')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'overdue' ? '#ef4444' : '#fee2e2',
          color: statusFilter === 'overdue' ? 'white' : '#991b1b',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        🔴 Overdue ({counts.overdue})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'completed' ? 'active' : ''}`}
        onClick={() => setStatusFilter('completed')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'completed' ? '#10b981' : '#d1fae5',
          color: statusFilter === 'completed' ? 'white' : '#065f46',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        ✅ Completed ({counts.completed})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'qc-pending' ? 'active' : ''}`}
        onClick={() => setStatusFilter('qc-pending')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'qc-pending' ? '#f59e0b' : '#fef3c7',
          color: statusFilter === 'qc-pending' ? 'white' : '#92400e',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        🎛️ QC Pending ({counts.qcPending})
      </button>
      <button 
        className={`filter-chip ${statusFilter === 'verified' ? 'active' : ''}`}
        onClick={() => setStatusFilter('verified')}
        style={{
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          background: statusFilter === 'verified' ? '#10b981' : '#d1fae5',
          color: statusFilter === 'verified' ? 'white' : '#065f46',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        ✓ Verified ({counts.verified})
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="worker-tasks-modal" onClick={(e) => e.stopPropagation()}>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading tasks for {worker?.name}...</p>
          </div>
        </div>
      </div>
    );
  }

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const ongoingTasks = tasks.filter(t => t.status === 'ongoing');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const overdueTasks = tasks.filter(t => t.status === 'pending' && t.dueDate && t.dueDate < new Date());

  return (
    <div className="modal-overlay worker-tasks-overlay" onClick={onClose}>
      <div className="worker-tasks-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="worker-tasks-header">
          <div className="worker-info-large">
            <div className="worker-avatar-large">
              {worker.name?.charAt(0).toUpperCase()}
            </div>
            <div className="worker-stats-large">
              <h2>{worker.name}</h2>
              <p>{worker.role?.replace('-', ' ')}</p>
              <div className="worker-summary-stats">
                <span>📋 {tasks.length} Total</span>
                <span>⏳ {pendingTasks.length} Pending</span>
                <span>⚙️ {ongoingTasks.length} Ongoing</span>
                <span>✅ {completedTasks.length} Done</span>
                <span className="overdue-stat">🔴 {overdueTasks.length} Overdue</span>
              </div>
            </div>
          </div>
          <div className="view-toggle-large">
            <button className={`view-btn-large ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>📱 Grid View</button>
            <button className={`view-btn-large ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>📋 List View</button>
            <button className="close-modal-btn-large" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tasks Content */}
        <div className="worker-tasks-content">
          {/* NEW: Filter Chips */}
          <FilterChips />

          {filteredTasks.length === 0 ? (
            <div className="no-tasks-message">
              <div className="no-tasks-icon">📭</div>
              <h3>No Tasks Found</h3>
              <p>No tasks match the selected filter.</p>
              {statusFilter !== 'all' && (
                <button 
                  onClick={() => setStatusFilter('all')}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: '#800020',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Show All Tasks
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="worker-tasks-grid">
              {filteredTasks.map(task => (
                <div
                  key={task.id}
                  className="worker-task-grid-card"
                  style={{ borderTop: `4px solid ${getTaskColor(task)}` }}
                  onMouseEnter={(e) => handleTaskHover(e, task)}
                  onMouseLeave={handleTaskLeave}
                >
                  <div className="task-status-indicator" style={{ background: getTaskColor(task) }}>
                    <span>{getStatusText(task)}</span>
                  </div>
                  <h4 className="task-grid-title">{task.title}</h4>
                  <div className="task-grid-project">📁 {task.projectName || 'Unknown Project'}</div>
                  <div className="task-grid-meta">
                    {task.startDateObj && <span>🚀 {formatDate(task.startDateObj)}</span>}
                    {task.endDateObj && <span>🏁 {formatDate(task.endDateObj)}</span>}
                  </div>
                  {task.assignedToNames && task.assignedToNames.length > 0 && (
                    <div className="task-grid-assigned">
                      👥 {Array.isArray(task.assignedToNames) ? task.assignedToNames.join(', ') : task.assignedToNames}
                    </div>
                  )}
                  {task.assignmentNote && (
                    <div className="task-grid-note">💬 {task.assignmentNote.substring(0, 50)}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="worker-tasks-list">
              <div className="list-header">
                <div>Task Name</div>
                <div>Project</div>
                <div>Status</div>
                <div>Start Date</div>
                <div>End Date</div>
                <div>Assigned To</div>
              </div>
              <div className="list-body">
                {filteredTasks.map(task => (
                  <div
                    key={task.id}
                    className="list-item"
                    onMouseEnter={(e) => handleTaskHover(e, task)}
                    onMouseLeave={handleTaskLeave}
                  >
                    <div className="list-item-title">{task.title}</div>
                    <div className="list-item-project">{task.projectName || 'N/A'}</div>
                    <div className="list-item-status">
                      <span className="status-badge" style={{ background: getTaskColor(task) }}>
                        {getStatusText(task)}
                      </span>
                    </div>
                    <div className="list-item-start">{formatDate(task.startDateObj)}</div>
                    <div className="list-item-end">{formatDate(task.endDateObj)}</div>
                    <div className="list-item-assigned">
                      {Array.isArray(task.assignedToNames) ? task.assignedToNames.join(', ') : task.assignedToName || 'Unassigned'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="worker-tasks-footer">
          <div className="footer-stats">
            <span>📊 Showing: {filteredTasks.length} / {tasks.length} tasks</span>
            {statusFilter !== 'all' && (
              <button 
                onClick={() => setStatusFilter('all')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#800020',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Clear Filter
              </button>
            )}
          </div>
          <button className="close-footer-btn" onClick={onClose}>Close</button>
        </div>

        {/* Tooltip */}
        {hoveredTask && (
          <div 
            className="worker-task-tooltip"
            style={{ 
              position: 'fixed', 
              top: hoverPosition.y, 
              left: hoverPosition.x,
              zIndex: 10000
            }}
          >
            <div className="tooltip-header">
              <strong>{hoveredTask.title}</strong>
              <span className={`tooltip-status ${hoveredTask.status}`}>
                {getStatusText(hoveredTask)}
              </span>
            </div>
            <div className="tooltip-body">
              <p className="tooltip-description">{hoveredTask.description || 'No description'}</p>
              <div className="tooltip-details">
                <div className="detail-row">📁 Project: {hoveredTask.projectName || 'Unknown'}</div>
                <div className="detail-row">👥 Assigned to: {Array.isArray(hoveredTask.assignedToNames) ? hoveredTask.assignedToNames.join(', ') : hoveredTask.assignedToName || 'Unassigned'}</div>
                <div className="detail-row">🚀 Start: {formatDate(hoveredTask.startDateObj)}</div>
                <div className="detail-row">🏁 End: {formatDate(hoveredTask.endDateObj)}</div>
                {hoveredTask.assignmentNote && (
                  <div className="detail-row note">💬 Note: {hoveredTask.assignmentNote}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerTasksModal;